import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { QueryKeys } from '../QueryKeys';
import { getVenueCategories } from 'src/api/getVenueCategories/getVenueCategories';
import {
  IGetVenueCategoriesResponseData,
  INestedVenueCategory,
  IVenueCategory,
} from 'src/api/getVenueCategories/types';
import { useMemo } from 'react';
import { buildNestedVenueCategories } from 'src/utils/venueCategories';
import { flattenHierarchy } from 'src/helpers/flattenHierarchy';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';

/**
 * Hook for fetching and shaping venue categories
 * @returns {UseQueryResult} - Query result with:
 *   - query.data.categories: raw flat array data
 *   - nestedCategories: hierarchical tree structure
 *   - orderedFlatCategories: flat array in hierarchical order (Uncategorised, then parent > children)
 */

export const useVenueCategories = (): UseQueryResult<IGetVenueCategoriesResponseData> & {
  nestedCategories: INestedVenueCategory[];
  orderedFlatCategories: IVenueCategory[];
} => {
  const { showSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: [getBrandId(), QueryKeys.venueCategories],
    queryFn: getVenueCategories,
    onError: () =>
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst fetching venue categories',
      }),
  });

  const nestedCategories = useMemo(() => {
    if (!query.data?.categories) {
      return [];
    }
    return buildNestedVenueCategories(query.data.categories);
  }, [query.data?.categories]);

  const orderedFlatCategories = useMemo(() => {
    return flattenHierarchy(nestedCategories);
  }, [nestedCategories]);

  return {
    ...query,
    nestedCategories,
    orderedFlatCategories,
  };
};
